/**
 * Core-100 R4 wave 2 (CORE-AV D): the load index of a management surface comes from the SAME assembler as the
 * Dayboard and the calendar.
 *
 * Constitution CORE-AV: "One implementation SHALL serve every surface. A load figure that differs between the
 * calendar and the board is not a measurement." and its outputs table lists "Dayboard · calendar · management
 * surfaces". Shop Health used to compute the index from two analytics RPCs (`kpi_shop_capacity`,
 * `kpi_shop_load_inputs`) with their own capacity model and a head-count staffing ratio, so the same shop-day could
 * read differently there than on the board. This hook loads exactly the inputs the Dayboard hands
 * `assembleOBLIDay` — the full technician rows with that day's partial-day absences, the mechanical work-role
 * answer, shop hours and holidays, and the day's tickets with their job lines — and returns the assembled day.
 * Until every input is known the index is "Capacity unavailable" (capacityInputsReady=false), never 0 or 100.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { assembleOBLIDay, type OBLIDay } from '@/lib/obli/obliDayInputs';
import { mapShopHoursToDayNumbers } from '@/lib/shopHours/mapCalendarHours';
import { useMechanicalRoster } from '@/hooks/useMechanicalTechnicianIds';
import { useTechnicianAbsenceIntervals } from '@/hooks/useTechnicianAbsenceIntervals';
import { withAbsenceIntervals } from '@/utils/capacity/absenceIntervals';
import { clock } from '@/utils/clock';

/** The same job-line shape the Dayboard's ticket read carries (stress counts + effective workload). */
export const OBLI_DAY_TICKET_COLUMNS =
  '*, ticket_jobs(minutes_override, separate_time, actual_minutes, job_status, job:jobs(*))';

function dayWindow(dayLabel: string): { from: string; to: string } {
  const [y, m, d] = dayLabel.split('-').map(Number);
  // A ticket with no scheduled_date is placed on its shop-local start day by the assembler; read a window wide enough
  // for every time zone and let `obliDayTickets` decide.
  return { from: new Date(Date.UTC(y, m - 1, d - 1)).toISOString(), to: new Date(Date.UTC(y, m - 1, d + 2)).toISOString() };
}

export interface OBLIShopDayResult {
  day: OBLIDay | null;
  capacityInputsReady: boolean;
}

export function useOBLIShopDay(shopId: string | null | undefined, dayLabel: string, shopTimezone: string | null | undefined): OBLIShopDayResult {
  const enabled = !!shopId && !!dayLabel;
  const roster = useMechanicalRoster(shopId);
  const absences = useTechnicianAbsenceIntervals(shopId, dayLabel, dayLabel);

  const technicians = useQuery({
    queryKey: ['obli-shop-day', 'technicians', shopId],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('technicians').select('*').eq('shop_id', shopId as string);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });
  const profile = useQuery({
    queryKey: ['obli-shop-day', 'shop-profile', shopId],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('shop_profile').select('hours, holidays').eq('shop_id', shopId as string).maybeSingle();
      if (error) throw error;
      const holidays = Array.isArray(data?.holidays) ? (data!.holidays as any[]).map((h) => h?.date || h) : [];
      return { shopHours: mapShopHoursToDayNumbers((data?.hours ?? {}) as never), holidays: holidays as string[] };
    },
  });
  const tickets = useQuery({
    queryKey: ['obli-shop-day', 'tickets', shopId, dayLabel],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const w = dayWindow(dayLabel);
      const { data, error } = await supabase
        .from('tickets')
        .select(OBLI_DAY_TICKET_COLUMNS)
        .eq('shop_id', shopId as string)
        .or(`scheduled_date.eq.${dayLabel},and(scheduled_date.is.null,start_time.gte.${w.from},start_time.lt.${w.to})`);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const capacityInputsReady = technicians.isSuccess && profile.isSuccess && tickets.isSuccess
    && roster.status === 'known' && absences.status === 'known';

  const day = useMemo(() => {
    if (!enabled || !technicians.data || !profile.data || !tickets.data) return null;
    const [y, m, d] = dayLabel.split('-').map(Number);
    return assembleOBLIDay({
      technicians: withAbsenceIntervals(technicians.data, absences.byTech),
      tickets: tickets.data,
      selectedDate: new Date(y, m - 1, d, 12, 0, 0),
      dayLabel,
      shopTimezone,
      shopHours: profile.data.shopHours,
      holidays: profile.data.holidays,
      mechanicalTechnicianIds: roster.status === 'known' ? roster.ids : null,
      capacityInputsReady,
      nowMs: clock.nowMs(),
    });
  }, [enabled, technicians.data, profile.data, tickets.data, absences.byTech, dayLabel, shopTimezone, roster, capacityInputsReady]);

  return { day, capacityInputsReady };
}
